import { TIdentifierObject } from "@/constants/Test";
import type {
  IGetEquipmentParams,
  IGetBookingsParams,
  ICreateBookingRequest,
  IUpdateBookingRequest,
  ICancelBookingRequest,
  IUpdateStatusRequest,
  TGetEquipmentResponse,
  TGetBookingsResponse,
  TCreateBookingResponse,
} from "@modules/EquipmentBooking/types";
import type { TUvColorTableResponse } from "@/modules/UVVisualizer/types";
import { IConfig } from "@/type/router";
import http from "@services/axios";
import apiConfig from "@services/env";
import {
  IESDGenerateTestPlanRequest,
  IESDGetSettingsRequest,
  IESDImportTestPlanRequest,
  IESDPredictRequest,
  IESDProjectDataRequest,
  IESDSaveSettingsRequest,
  IFATrackerSummary,
  IFDDDCascadingFilterRequest,
  IFDDDFailureRateSummaryRequest,
  INewFindingsFilterParams,
  IPresetItem,
  IServerResponse,
  ITaskCenterProject,
  TAddEditReadoutRequest,
  TAddEditSequenceRequest,
  TAddFamilySpecRequest,
  TAddIssueRequest,
  TAssignOperatorsResponse,
  TAttachmentsByTestResponse,
  TBatchActionResponse,
  TBatchApproveRequest,
  TBatchApproveResponse,
  TBatchMarkCompletedRequest,
  TBatchMarkOngoingRequest,
  TBatchPassReadoutsRequest,
  TBatchResetRequest,
  TBatchSetVariablesRequest,
  TBatchSmartSummaryRequest,
  TBatchSmartSummaryResponse,
  TBuildConfigResponse,
  TBulkUploadRealDSNRequest,
  TBulkUploadRealDSNResponse,
  TChangeReadoutScheduleRequest,
  TCopyIssueRequest,
  TCopySequenceOptionsRequest,
  TCopySequenceOptionsResponse,
  TCopySequenceRequest,
  TCopyTestWaterfallRequest,
  TCreateFARecordResponse,
  TCreateFARequest,
  TCreateShareableLinkResponse,
  TCreateTestSpecRequest,
  TDataOverviewListResponse,
  TDeleteIssueRequest,
  TDeleteReadoutOptionsResponse,
  TDeleteSequenceOptionsResponse,
  TDeleteSequenceRequest,
  TDeviceTestWaterfallResponse,
  TDiscardFamilySpecRequest,
  TEditBuildConfigRequest,
  TEditFamilySpecRequest,
  TEditHolidaysRequest,
  TEditIssueRequest,
  TEditMilestonesRequest,
  TEditTestTimelineRequest,
  TEditTestWaterfallRequest,
  TESDGenerateTestPlanResponse,
  TESDGetSettingsResponse,
  TESDImportTestPlanResponse,
  TESDPredictResponse,
  TESDProjectDataResponse,
  TESDSaveSettingsResponse,
  TFADetailResponse,
  TFALogListResponse,
  TFamilySpecApproversResponse,
  TFamilySpecCommitteesResponse,
  TFamilySpecDetailResponse,
  TFamilySpecListResponse,
  TFamilySpecVersionHistoryResponse,
  TFATPCandidateLogsRequest,
  TFATPCheckOptionsResponse,
  TFATPConfigsRequest,
  TFATPConfigsResponse,
  TFATPGenealogyRequest,
  TFATPGenealogyResponse,
  TFATPParameterDataRequest,
  TFATPParameterDataResponse,
  TFATPParametersRequest,
  TFATPParametersResponse,
  TFATPRawExportRequest,
  TFATPRawExportStatusResponse,
  TFATPRawExportSubmissionResponse,
  TFatpReadoutsResponse,
  TFATPViewerReadoutTreeResponse,
  TFDDDCascadingFilterResponse,
  TFDDDFailureRateSummaryResponse,
  TGetHolidaysResponse,
  TGetShareableLinkResponse,
  TGroupingSequencesResponse,
  TIdentifierParams,
  TIdentifierResponse,
  TImportWaterfallRequest,
  TImportWaterfallResponse,
  TIssueDetailResponse,
  TIssueListForCopyResponse,
  TIssueListResponse,
  TIssueSummaryResponse,
  TMajorReadoutListResponse,
  TMarkWaivedRequest,
  TMergeIssueRequest,
  TNewFindingsFilterOptionsResponse,
  TNewFindingsListResponse,
  TOperatorCreateRequest,
  TOperatorListResponse,
  TOperatorPasswordResponse,
  TOPListResponse,
  TPresetListResponse,
  TReadoutDataByTestResponse,
  TReadoutDeviceListResponse,
  TReadoutFATPLogOptionsResponse,
  TReadoutListResponse,
  TReadoutMetadataResponse,
  TReadoutTestDataResponse,
  TReadoutTypeMasterListResponse,
  TRealDSNListResponse,
  TSaveBatchFatpMappingRequest,
  TSaveBatchFatpMappingResponse,
  TSaveReadoutRequest,
  TSmartSummaryRequest,
  TSmartSummaryResponse,
  TSubmitTestSpecSignOffRequest,
  TTaskCenterRawResponse,
  TTestAssignmentListResponse,
  TTestDurationListResponse,
  TTestFidelityEquipmentListResponse,
  TTestSettingsCheckMappingResponse,
  TTestSettingsEquipmentListResponse,
  TTestSettingsMeasurementMappingResponse,
  TTestSettingsReadoutListResponse,
  TTestSettingsSequenceListResponse,
  TTestSettingsTestVariableResponse,
  TTestSpecDetail,
  TTestSpecDetailResponse,
  TTestSpecListResponse,
  TTestSpecReadoutMappingsResponse,
  TTestSpecSequencesResponse,
  TTestStatusListResponse,
  TTestStatusMode,
  TTestSummaryResponse,
  TTestVariableSelectionsResponse,
  TTestWaterfallTimelineOverviewResponse,
  TUnlinkFindingRequest,
  TUpdateAllFARecordsRequest,
  TUpdateAllFARecordsResponse,
  TUpdateCheckMappingRequest,
  TUpdateFADetailRequest,
  TUpdateMeasurementMappingRequest,
  TUpdateRealDSNRequest,
  TUpdateTestAssignmentRequest,
  TUpdateTestStatusRequest,
  TUpdateTestVariableApplyStatusRequest,
  TUserInfoResponse,
  TUserListResponse,
  TUVVisualizerColorTableResponse,
} from "@type/axios"; // eslint-disable-line @typescript-eslint/no-unused-vars

// User Info — device identity headers (X-Device-Id, X-Device-Fingerprint) are
// attached automatically by the axios request interceptor in axios.ts.
// The backend uses these to detect new/unauthorized devices.
export const fetch_UserInfo = () => http.get<TUserInfoResponse>(apiConfig.fetch_UserInfo, { useSystemLoading: false });
// export const fetch_UserList = (tag: string) => http.get<TUserListResponse>(apiConfig.fetch_UserList, { params: { tag }, useSystemLoading: false });
// Router Config
// export const fetch_RouterConfig = () => http.get<TRouterConfigResponse>(apiConfig.fetch_RouterConfig, { useSystemLoading: false });
export const fetch_RouterConfig = () => http.get<IServerResponse<IConfig[]>>(apiConfig.fetch_RouterConfig, { useSystemLoading: false });

// Page Visit Telemetry — fire-and-forget; callers should swallow rejections since this
// has no business-data side effects to retry or surface to the user.
export const record_PageVisit = (data: { pageKey: string; path?: string }) => http.post<IServerResponse<null>>(apiConfig.record_PageVisit, data, { useSystemLoading: false });

// Identifier List
export const fetch_Project_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_Project_List, { params: data, useSystemLoading: false });
export const fetch_Phase_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_Phase_List, { params: data, useSystemLoading: false });
export const fetch_Build_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_Build_List, { params: data, useSystemLoading: false });
export const fetch_Test_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_Test_List, { params: data, useSystemLoading: false });
export const fetch_Config_List = (data: TIdentifierParams | undefined) => http.get<TBuildConfigResponse>(apiConfig.fetch_Config_List, { params: data });
export const fetch_PhaseBuild_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_PhaseBuild_List, { params: data, useSystemLoading: false });

export const fetch_FamilySubcategory_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_FamilySubcategory_List, { params: data, useSystemLoading: false });

// Shareable Link
export const create_ShareableLink = (data: { requestParameter: string; description: string; feature: string }) => http.post<TCreateShareableLinkResponse>(apiConfig.create_ShareableLink, data, { useSystemLoading: false });
export const fetch_ShareableLink = (uuid: string) => http.get<TGetShareableLinkResponse>(apiConfig.get_ShareableLink, { params: { uuid } });
// General
export const fetch_TestWithReadout = (data: TIdentifierParams) => http.get<TTestDurationListResponse>(apiConfig.fetch_TestWithReadout, { params: data });

// Test Spec
export const fetch_TestSpec_SpecList = (data: TIdentifierObject) => http.get<TTestSpecListResponse>(apiConfig.fetch_TestSpec_SpecList, { params: data });
export const fetch_TestSpec_SpecDetail = (data: TIdentifierObject) => http.get<TTestSpecDetailResponse>(apiConfig.fetch_TestSpec_SpecDetail, { params: data });
// Test Settings panel: sequence names + per-readout check/measurement mappings.
// sequenceId is the grouping-sequence NAME (e.g. "DEFAULT"), not the integer DB id.
export const fetch_TestSpec_Sequences = (data: TIdentifierObject) => http.get<TTestSpecSequencesResponse>(apiConfig.fetch_TestSpec_Sequences, { params: data });
export const fetch_TestSpec_ReadoutMappings = (data: TIdentifierObject & { sequenceId: string }) => http.get<TTestSpecReadoutMappingsResponse>(apiConfig.fetch_TestSpec_ReadoutMappings, { params: data });
export const create_TestSpec_SpecDetail = (data: TCreateTestSpecRequest) => http.post<TTestSpecListResponse>(apiConfig.create_TestSpec_SpecDetail, data, { useSysRefresh: true });
export const update_TestSpec_SpecDetail = (data: TIdentifierObject & { testItem: Partial<TTestSpecDetail> }) => http.post<IServerResponse>(apiConfig.update_TestSpec_SpecDetail, data, { useSysRefresh: true });
export const submit_TestSpec_SignOff = (data: TSubmitTestSpecSignOffRequest) => http.post<IServerResponse>(apiConfig.submit_TestSpec_SignOff, data, { useSysRefresh: true });
export const delete_TestSpec_SpecDetail = (data: TIdentifierObject) => http.post<IServerResponse>(apiConfig.delete_TestSpec_SpecDetail, data, { useSysRefresh: true });
export const reset_TestSpec_SpecDetail = (data: TIdentifierObject) => http.post<IServerResponse>(apiConfig.reset_TestSpec_SpecDetail, data, { useSysRefresh: true });
// Download the test spec list as an Excel workbook (Revision + Reliability Spec tabs). See CR-287905496 (DT-RadarPlus-Backend).
export const download_TestSpec_List = (data: TIdentifierObject) => http.get<Blob>(apiConfig.download_TestSpec_List, { params: data, responseType: "blob" });
// Family Spec — see CR-286038137 (DT-RadarPlus-Backend-Model)
export const fetch_FamilySpec_List = (data: { familyId: string; subcategoryId: string }) => http.get<TFamilySpecListResponse>(apiConfig.fetch_FamilySpec_List, { params: data });
// Pass versionId to load a specific version (approved snapshot or a draft); otherwise the latest row for the test.
export const fetch_FamilySpec_Detail = (data: { familyId?: string; subcategoryId?: string; testId?: string; versionId?: string }) => http.get<TFamilySpecDetailResponse>(apiConfig.fetch_FamilySpec_Detail, { params: data });
export const fetch_FamilySpec_VersionHistory = (data: { familyId: string; subcategoryId: string; testId: string }) => http.get<TFamilySpecVersionHistoryResponse>(apiConfig.fetch_FamilySpec_VersionHistory, { params: data });
// Approver/committee options are scoped by whatever identifier the caller has: FamilySpec passes
// { familyId }, TestSpec sign-off passes the build identifier ({ projectId, testLocationId, phaseId, buildId }).
export const fetch_FamilySpec_Approvers = (data?: Record<string, string | undefined>) => http.get<TFamilySpecApproversResponse>(apiConfig.fetch_FamilySpec_Approvers, { params: data, useSystemLoading: false });
export const fetch_FamilySpec_Committees = (data?: Record<string, string | undefined>) => http.get<TFamilySpecCommitteesResponse>(apiConfig.fetch_FamilySpec_Committees, { params: data, useSystemLoading: false });
export const add_FamilySpec = (data: TAddFamilySpecRequest) => http.post<IServerResponse>(apiConfig.add_FamilySpec, data, { useSysRefresh: true });
export const edit_FamilySpec = (data: TEditFamilySpecRequest) => http.post<IServerResponse>(apiConfig.edit_FamilySpec, data, { useSysRefresh: true });
export const discard_FamilySpec = (data: TDiscardFamilySpecRequest) => http.post<IServerResponse>(apiConfig.discard_FamilySpec, data, { useSysRefresh: true });
// Download the family spec list as an Excel workbook (Revision + Reliability Spec tabs). See CR-287905496 (DT-RadarPlus-Backend).
export const download_FamilySpec_List = (data: { familyId: string; subcategoryId: string }) => http.get<Blob>(apiConfig.download_FamilySpec_List, { params: data, responseType: "blob" });
// TestWaterfall
export const fetch_TestWaterfall_List = (data: TIdentifierObject) => http.get<TDeviceTestWaterfallResponse>(apiConfig.fetch_TestWaterfall_List, { params: data });
export const fetch_TestWaterfall_TimelineOverview = (data: TIdentifierObject) => http.get<TTestWaterfallTimelineOverviewResponse>(apiConfig.fetch_TestWaterfall_TimelineOverview, { params: data, useSystemLoading: false });
export const update_TestWaterfall_DSNTest = (data: TEditTestWaterfallRequest) => http.post<IServerResponse>(apiConfig.update_TestWaterfall_DSNTest, data, { useSysRefresh: true });
export const update_TestWaterfall_Config = (data: TEditBuildConfigRequest) => http.post<IServerResponse>(apiConfig.update_TestWaterfall_Config, data, { useSysRefresh: true });
export const copy_TestWaterfall_List = (data: TCopyTestWaterfallRequest) => http.post<IServerResponse>(apiConfig.copy_TestWaterfall_List, data, { useSysRefresh: true });
export const validate_TestWaterfall_Timeline = (data: TIdentifierObject) => http.post<IServerResponse>(apiConfig.validate_TestWaterfall_Timeline, data, {});
export const save_TestWaterfall_ExecutionTimeline = (data: TIdentifierObject) => http.post<IServerResponse>(apiConfig.save_TestWaterfall_ExecutionTimeline, data, {});
export const update_TestWaterfall_Milestones = (data: TEditMilestonesRequest) => http.post<IServerResponse>(apiConfig.update_TestWaterfall_Milestones, data, { useSysRefresh: true });
export const fetch_TestWaterfall_Holidays = (data: TIdentifierObject) => http.get<TGetHolidaysResponse>(apiConfig.fetch_TestWaterfall_Holidays, { params: data });
export const update_TestWaterfall_Holidays = (data: TEditHolidaysRequest) => http.post<IServerResponse>(apiConfig.update_TestWaterfall_Holidays, data, { useSysRefresh: true });
export const update_TestWaterfall_Timeline = (data: TEditTestTimelineRequest) => http.post<IServerResponse>(apiConfig.update_TestWaterfall_Timeline, data, { useSysRefresh: true });

// Task Center / Data Input
/**
 * Parse the backend's pipe-delimited build identifiers (Smithy: OngoingTaskItem) into fields.
 * `buildIdentifierIds` drives navigation ("family|product|testLocation|phase|build"); the
 * human-readable `buildIdentifierNames` drives the display name, falling back to ids if absent.
 * Exported for unit testing.
 */
export function parseTaskCenterProject(raw: TTaskCenterRawResponse["data"][number]): ITaskCenterProject {
  const [familyId = "", projectId = "", testLocationId = "", phaseId = "", buildId = ""] = (raw.buildIdentifierIds ?? "").split("|");
  // Prefer human-readable names for display; fall back to the ids so the name is never blank.
  const [, productName = projectId, , phaseName = phaseId, buildName = buildId] = (raw.buildIdentifierNames ?? "").split("|");
  // Build a display name: "product - phase build"
  const projectName = `${productName} - ${phaseName} ${buildName}`.trim();
  return {
    familyId,
    projectId,
    testLocationId,
    phaseId,
    buildId,
    projectName,
    realDsnProgress: { completed: raw.realDsnCompleted, total: raw.realDsnTotal },
    testInputs: { completed: raw.testInputsCompleted, total: raw.testInputsTotal },
    // Sort by due date ascending (soonest first) so the most urgent tests surface at the top.
    testsDueSoon: (raw.testsDueSoon ?? [])
      .map(item => ({
        testId: item.testCode,
        testName: item.testNameAndLevel,
        dueDate: item.date,
      }))
      .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || "")),
  };
}

export const fetch_TaskCenter_Projects = () =>
  http.get<TTaskCenterRawResponse>(apiConfig.fetch_TaskCenter_Projects, { useSystemLoading: true }).then(res => ({
    ...res,
    data: Array.isArray(res.data) ? res.data.map(parseTaskCenterProject) : [],
  }));
// Real DSN

export const fetch_RealDSN_List = (data: TIdentifierObject) => http.get<TRealDSNListResponse>(apiConfig.fetch_RealDSN_List, { params: data, useSystemLoading: true });
export const update_RealDSN_Item = (data: TUpdateRealDSNRequest) => http.post<IServerResponse>(apiConfig.update_RealDSN_Item, data, { useSysRefresh: true });
export const delete_RealDSN_Item = (data: TIdentifierObject & { id: string }) => http.post<IServerResponse>(apiConfig.delete_RealDSN_Item, data, { useSysRefresh: true });
export const download_RealDSN_Template = (data: TIdentifierObject) => http.get<Blob>(apiConfig.download_RealDSN_Template, { params: data, responseType: "blob" });
export const upload_RealDSN_BulkData = (data: TBulkUploadRealDSNRequest) => {
  const formData = new FormData();
  formData.append("file", data.file);

  // Build query parameters from identifier
  const params = new URLSearchParams();
  if (data.projectId) params.append("projectId", data.projectId);
  if (data.testLocationId) params.append("testLocationId", data.testLocationId);
  if (data.phaseId) params.append("phaseId", data.phaseId);
  if (data.buildId) params.append("buildId", data.buildId);

  const url = `${apiConfig.upload_RealDSN_BulkData}?${params.toString()}`;

  return http.post<TBulkUploadRealDSNResponse>(url, formData, {
    useSysRefresh: true,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Import Waterfall
export const download_TestWaterfall_Template = (data: TIdentifierObject) => http.get<Blob>(apiConfig.download_TestWaterfall_Template, { params: data, responseType: "blob" });

// The BE runs an unbounded, build-wide query per grain and buffers the entire workbook before it
// responds, so nothing reaches the client early and a large build can easily outlast the shared
// 30s default in axios.ts. Raise the ceiling for this one endpoint only.
//
// Nothing bounds the query itself server-side — no driver-level or statement timeout applies — but
// in any deployed stage a 60s load balancer idle timeout cuts the request first. The entire wait
// counts as idle, because the response is assembled as one buffer and no bytes flow to reset the
// timer. So five minutes here is not the operative ceiling in deployed stages and cannot rescue a
// request the load balancer has already dropped; it keeps the client from being the thing that
// fails first, and it is the only bound during local development, where no load balancer sits in
// front. Do not lower it to match the 60s.
const READOUT_DATA_EXPORT_TIMEOUT_MS = 300000;

// The BE's ReadoutExportScope (import_waterfall.smithy) accepts CHECK, MEASUREMENT or ALL, and
// writes one tab per requested grain. Only the two single-grain values are offered here: each UI
// action exports one grain, and a user who wants both runs both actions, so no caller asks for ALL.
export type TReadoutExportGrain = "CHECK" | "MEASUREMENT";

// Build-level readout data export. Returns the raw .xlsx body, not a BaseResponse envelope.
//
// `grain` is required rather than defaulted because omitting `scope` makes the BE substitute ALL,
// which runs both unbounded build-wide queries back to back: ~20s for checks and ~10s for
// measurements, so ~30s together. On the worst-case build in prod, ALL measures 42.3s of server
// time — about 70% of the 60s idle budget above, with no headroom for a slower day. Requesting one
// grain per request keeps each one comfortably under it, so splitting the export by grain buys
// reliability and not only speed. An unrecognized value is rejected with a 400 rather than
// defaulted, so these literals must match the enum exactly.
//
// `useSystemLoading: false` opts out of the app-wide GlobalLoading overlay, which the axios
// instance turns on by default. That overlay is `position: fixed` and covers the whole viewport,
// so leaving it on would freeze the entire UI for the minutes this export can take — the user
// could not navigate, filter, or read the table while waiting on a background download. The
// calling component shows a non-blocking progress toast instead. Scoped here rather than in
// axios.ts on purpose: the default is correct for the short requests everywhere else.
export const download_ReadoutData = (data: TIdentifierObject, grain: TReadoutExportGrain) =>
  http.get<Blob>(apiConfig.download_ReadoutData, {
    params: { ...data, scope: grain },
    responseType: "blob",
    timeout: READOUT_DATA_EXPORT_TIMEOUT_MS,
    useSystemLoading: false,
  });

export const import_TestWaterfall = (data: TImportWaterfallRequest) => {
  const formData = new FormData();
  formData.append("file", data.file);

  const params = new URLSearchParams();
  if (data.projectId) params.append("projectId", data.projectId);
  if (data.testLocationId) params.append("testLocationId", data.testLocationId);
  if (data.phaseId) params.append("phaseId", data.phaseId);
  if (data.buildId) params.append("buildId", data.buildId);

  const url = `${apiConfig.import_TestWaterfall}?${params.toString()}`;

  return http.post<TImportWaterfallResponse>(url, formData, {
    useSysRefresh: true,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Test Status
export const fetch_TestStatus_List = (data: TIdentifierObject & { mode?: TTestStatusMode }) => http.get<TTestStatusListResponse>(apiConfig.fetch_TestStatus_List, { params: data });

// FDDD (Failure Data Deep Dive)
export const fetch_FDDD_CascadingFilterOptions = (data: IFDDDCascadingFilterRequest) => http.post<TFDDDCascadingFilterResponse>(apiConfig.fetch_FDDD_CascadingFilterOptions, { ...data, searchQuery: undefined }, { useSystemLoading: false });
export const fetch_FDDD_FailureRateSummary = (data: IFDDDFailureRateSummaryRequest) => http.post<TFDDDFailureRateSummaryResponse>(apiConfig.fetch_FDDD_FailureRateSummary, data, { useSystemLoading: false });
/** Parse API wire format (requestParameter as JSON string) into internal IPresetItem (path as string[]). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parsePresetWireItem = (item: any): IPresetItem => {
  const { requestParameter, ...rest } = item;
  return { ...rest, path: requestParameter ? JSON.parse(requestParameter) : (rest.path ?? []) };
};
/** Fetch presets for a given feature. API returns `requestParameter` as a JSON string; we parse it into `path: string[]`. */
export const fetch_PresetList = (feature: string) =>
  http.get<TPresetListResponse>(apiConfig.fetch_PresetList, { params: { feature }, useSystemLoading: false }).then(res => ({
    ...res,
    data: Array.isArray(res.data) ? res.data.map(parsePresetWireItem) : res.data,
  }));
/** Bulk replace — serializes `path` to `requestParameter` (JSON string) before sending. */
export const update_Presets = (data: IPresetItem[], feature: string) =>
  http.post<TPresetListResponse>(apiConfig.update_Presets, { data: data.map(({ path, ...rest }) => ({ ...rest, requestParameter: JSON.stringify(path) })), feature }, { useSystemLoading: false }).then(res => ({
    ...res,
    data: Array.isArray(res.data) ? res.data.map(parsePresetWireItem) : res.data,
  }));
// Test Summary / Reports
export const fetch_TestSummary_Data = (data: TIdentifierObject) => http.get<TTestSummaryResponse>(apiConfig.fetch_TestSummary_Data, { params: data });
export const fetch_IssueSummary = (data: TIdentifierObject & { groupingSequence?: string; page?: number; pageSize?: number }) => http.get<TIssueSummaryResponse>(apiConfig.fetch_IssueSummary, { params: data });
export const fetch_IssueDetail = (data: TIdentifierObject & { issueId: number; page?: number; pageSize?: number }) => http.get<TIssueDetailResponse>(apiConfig.fetch_IssueDetail, { params: data });
export const fetch_GroupingSequences = (data: TIdentifierObject) => http.get<TGroupingSequencesResponse>(apiConfig.fetch_GroupingSequences, { params: data, useSystemLoading: false });
export const fetch_ReadoutDataByTest = (data: TIdentifierObject & { groupingSequence?: string }) => http.get<TReadoutDataByTestResponse>(apiConfig.fetch_ReadoutDataByTest, { params: data, useSystemLoading: false });
export const fetch_AttachmentsByTest = (data: TIdentifierObject & { groupingSequence?: string }) => http.get<TAttachmentsByTestResponse>(apiConfig.fetch_AttachmentsByTest, { params: data });
export const update_TestSummary_FAStatus = (data: TIdentifierObject & { issueId: number; status: string }) => http.post<IServerResponse>(apiConfig.update_TestSummary_FAStatus, data, { useSysRefresh: true });
export const export_TestSummary_Excel = (data: TIdentifierObject) => http.get<Blob>(apiConfig.export_TestSummary_Excel, { params: data, responseType: "blob" });
export const download_TestReport = (data: TIdentifierObject & { groupingSequence?: string }) => http.get<Blob>(apiConfig.download_TestReport, { params: data, responseType: "blob" });
export const download_TestSummary_MeasurementData = (data: TIdentifierObject) => http.get<Blob>(apiConfig.download_TestSummary_MeasurementData, { params: data, responseType: "blob" });

// Operator Management (admin)
export const fetch_OperatorList = () => http.get<TOperatorListResponse>(apiConfig.fetch_OperatorList, { useSystemLoading: true });
export const create_Operator = (data: TOperatorCreateRequest) => http.post<TOperatorPasswordResponse>(apiConfig.create_Operator, data, { useSysRefresh: true });
export const delete_Operator = (data: { username: string }) => http.post<IServerResponse>(apiConfig.delete_Operator, data, { useSysRefresh: true });
export const activate_Operator = (data: { username: string }) => http.post<IServerResponse>(apiConfig.activate_Operator, data, { useSysRefresh: true });
export const deactivate_Operator = (data: { username: string }) => http.post<IServerResponse>(apiConfig.deactivate_Operator, data, { useSysRefresh: true });
export const resetPassword_Operator = (data: { username: string }) => http.post<TOperatorPasswordResponse>(apiConfig.resetPassword_Operator, data, { useSysRefresh: true });
// Test Settings — Sequence
export const fetch_TestSettings_SequenceList = (data: TIdentifierObject) => http.get<TTestSettingsSequenceListResponse>(apiConfig.fetch_TestSettings_SequenceList, { params: data });
// Test Settings mutations refresh via the caller's explicit onSuccess/onRefresh callback
// (fetchAll / fetchReadouts / fetchCheckMapping). They intentionally do NOT set useSysRefresh
// — doing so would fire a second, broader refresh on top of the callback, double-calling
// get_grouping_sequences / get_readout_checks / get_readout_measurements.
export const add_TestSettings_Sequence = (data: TAddEditSequenceRequest) => http.post<IServerResponse>(apiConfig.add_TestSettings_Sequence, data);
export const edit_TestSettings_Sequence = (data: TAddEditSequenceRequest) => http.post<IServerResponse>(apiConfig.edit_TestSettings_Sequence, data);
export const delete_TestSettings_Sequence = (data: TDeleteSequenceRequest) => http.post<IServerResponse>(apiConfig.delete_TestSettings_Sequence, data);
export const copy_TestSettings_Sequence = (data: TCopySequenceRequest) => http.post<IServerResponse>(apiConfig.copy_TestSettings_Sequence, data);
export const fetch_TestSettings_CopySequenceOptions = (data: TCopySequenceOptionsRequest) => http.get<TCopySequenceOptionsResponse>(apiConfig.fetch_TestSettings_CopySequenceOptions, { params: data });
export const fetch_TestSettings_DeleteSequenceOptions = (data: TIdentifierObject) => http.get<TDeleteSequenceOptionsResponse>(apiConfig.fetch_TestSettings_DeleteSequenceOptions, { params: data });

// Test Settings — Readout
export const fetch_TestSettings_ReadoutList = (data: Omit<TIdentifierObject, "sequenceId"> & { sequenceId: number }) => http.get<TTestSettingsReadoutListResponse>(apiConfig.fetch_TestSettings_ReadoutList, { params: data });
export const add_TestSettings_Readout = (data: TAddEditReadoutRequest) => http.post<IServerResponse>(apiConfig.add_TestSettings_Readout, data);
export const edit_TestSettings_Readout = (data: TAddEditReadoutRequest) => http.post<IServerResponse>(apiConfig.edit_TestSettings_Readout, data);
export const delete_TestSettings_Readout = (data: { sequenceId: number; selectedOrders: number[] }) => http.post<IServerResponse>(apiConfig.delete_TestSettings_Readout, data);
export const fetch_TestSettings_DeleteReadoutOptions = (data: { sequenceId: number }) => http.get<TDeleteReadoutOptionsResponse>(apiConfig.fetch_TestSettings_DeleteReadoutOptions, { params: data });
export const download_TestSettings_ReadoutTemplate = (data: Omit<TIdentifierObject, "sequenceId"> & { sequenceId: number }) =>
  http.get<{ fileContent: string; fileName: string }>(apiConfig.download_TestSettings_ReadoutTemplate, { params: data });
export const upload_TestSettings_Readouts = (data: Omit<TIdentifierObject, "sequenceId"> & { sequenceId: number; file: File }) => {
  const formData = new FormData();
  formData.append("file", data.file);
  const params = new URLSearchParams();
  if (data.projectId) params.append("projectId", data.projectId);
  if (data.testLocationId) params.append("testLocationId", data.testLocationId);
  if (data.phaseId) params.append("phaseId", data.phaseId);
  if (data.buildId) params.append("buildId", data.buildId);
  if (data.testId) params.append("testId", data.testId);
  params.append("sequenceId", String(data.sequenceId));
  return http.post<IServerResponse>(`${apiConfig.upload_TestSettings_Readouts}?${params.toString()}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const fetch_TestSettings_ReadoutTypeMaster = () => http.get<TReadoutTypeMasterListResponse>(apiConfig.fetch_TestSettings_ReadoutTypeMaster, { useSystemLoading: false });

// Test Settings — Check Mapping
export const fetch_TestSettings_CheckMapping = (data: { sequenceId: number }) => http.get<TTestSettingsCheckMappingResponse>(apiConfig.fetch_TestSettings_CheckMapping, { params: data });
export const update_TestSettings_CheckMapping = (data: TUpdateCheckMappingRequest) => http.post<IServerResponse>(apiConfig.update_TestSettings_CheckMapping, data);
export const reset_TestSettings_CheckMapping = (data: { sequenceId: number }) => http.post<IServerResponse>(apiConfig.reset_TestSettings_CheckMapping, data);

// Test Settings — Measurement Mapping
export const fetch_TestSettings_MeasurementMapping = (data: { sequenceId: number }) => http.get<TTestSettingsMeasurementMappingResponse>(apiConfig.fetch_TestSettings_MeasurementMapping, { params: data });
export const update_TestSettings_MeasurementMapping = (data: TUpdateMeasurementMappingRequest) => http.post<IServerResponse>(apiConfig.update_TestSettings_MeasurementMapping, data);

// Test Settings — Test Variable
export const fetch_TestSettings_TestVariables = (data: TIdentifierObject) => http.get<TTestSettingsTestVariableResponse>(apiConfig.fetch_TestSettings_TestVariables, { params: data });
export const update_TestSettings_TestVariableApplyStatus = (data: TUpdateTestVariableApplyStatusRequest) => http.post<IServerResponse>(apiConfig.update_TestSettings_TestVariableApplyStatus, data, { useSysRefresh: true });

// Test Settings — Equipment
export const fetch_TestSettings_EquipmentList = (data: TIdentifierObject) => http.get<TTestSettingsEquipmentListResponse>(apiConfig.fetch_TestSettings_EquipmentList, { params: data, useSystemLoading: false });
/** Fetch equipment list from Test Fidelity by CM (Contract Manufacturer).
 *  Response shape: { isSuccess, code, equipments: ITestFidelityEquipmentItem[] } at root level.
 */
export const fetch_TestSettings_EquipmentListForSequence = (cm: string) => http.get<TTestFidelityEquipmentListResponse>(apiConfig.fetch_TestSettings_EquipmentListForSequence, { params: { cm }, useSystemLoading: false });

// Test Assignment
export const fetch_TestAssignment_List = (data: TIdentifierObject) => http.get<TTestAssignmentListResponse>(apiConfig.fetch_TestAssignment_List, { params: data, useSystemLoading: true });
export const update_TestAssignment = (data: TUpdateTestAssignmentRequest) => http.post<TAssignOperatorsResponse>(apiConfig.update_TestAssignment, data, { useSysRefresh: true });
export const fetch_OP_List = () => http.get<TOPListResponse>(apiConfig.fetch_OP_List, { useSystemLoading: false });
// Issue List
export const fetch_IssueList = (data: TIdentifierObject) => http.get<TIssueListResponse>(apiConfig.fetch_IssueList, { params: data });
export const add_Issue = (data: TAddIssueRequest) => http.post<IServerResponse>(apiConfig.add_Issue, data, { useSysRefresh: true });
export const edit_Issue = (data: TEditIssueRequest) => http.post<IServerResponse>(apiConfig.edit_Issue, data, { useSysRefresh: true });
export const delete_Issue = (data: TDeleteIssueRequest) => http.delete<IServerResponse>(apiConfig.delete_Issue, { data, useSysRefresh: true });
export const copy_Issue = (data: TCopyIssueRequest) => http.post<IServerResponse>(apiConfig.copy_Issue, data, { useSysRefresh: true });
export const merge_Issue = (data: TMergeIssueRequest) => http.post<IServerResponse>(apiConfig.merge_Issue, data, { useSysRefresh: true });
export const fetch_IssueListForCopy = (data: TIdentifierObject) => http.get<TIssueListForCopyResponse>(apiConfig.fetch_IssueListForCopy, { params: data, useSystemLoading: false });

// Repeated-key serializer for array query params (?issueIds=1&issueIds=2), the shape Smithy
// StringList query params expect. Empty/absent values are dropped. Declared here rather than beside
// its FA Tracker callers below because download_FATPCandidateLogs is now the first use in file
// order, and a `const` arrow function is not hoisted — referencing it above this line would throw
// at module init.
const repeatedKeyParamsSerializer = (params: Record<string, unknown>): string => {
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => v !== undefined && v !== null && v !== "" && parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`));
    } else if (value !== undefined && value !== null && value !== "") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  });
  return parts.join("&");
};

// Readout Input / Data Input
export const fetch_ReadoutInput_TestData = (data: TIdentifierObject) => http.get<TReadoutTestDataResponse>(apiConfig.fetch_ReadoutInput_TestData, { params: data });
export const fetch_ReadoutInput_Metadata = (data: TIdentifierObject) => http.get<TReadoutMetadataResponse>(apiConfig.fetch_ReadoutInput_Metadata, { params: data });
export const fetch_ReadoutInput_DeviceList = (data: TIdentifierObject) => http.get<TReadoutDeviceListResponse>(apiConfig.fetch_ReadoutInput_DeviceList, { params: data });
export const fetch_ReadoutInput_FATPLogOptions = (data: TIdentifierObject) => http.get<TReadoutFATPLogOptionsResponse>(apiConfig.fetch_ReadoutInput_FATPLogOptions, { params: data, useSysRefresh: false, useSystemLoading: false });

// The BE runs one Athena query plus up to 25 AME calls (~1.5s each), capped so the whole request
// stays inside the 60s ALB idle timeout. 75s here leaves the server's own failure to be the one the
// user sees, rather than the client giving up first on a request that would have succeeded.
const FATP_CANDIDATE_LOGS_TIMEOUT_MS = 75_000;

// Wide-format CSV of every candidate log for one FATP check — the comparison the log dropdown cannot
// show. Blob, not a BaseResponse envelope (see DownloadFatpCandidateLogs in fatp.smithy). `dsns` is a
// StringList query param, hence the repeated-key serializer. `checkCode` is camelCase here, unlike
// the lower-case `checkcode` fetch_ReadoutInput_FATPLogOptions takes just above — do not copy that
// call's params object. The system loading overlay is off so the user can keep working while it
// builds; the button owns its own spinner.
export const download_FATPCandidateLogs = (data: TFATPCandidateLogsRequest) =>
  http.get<Blob>(apiConfig.download_FATPCandidateLogs, {
    params: data,
    paramsSerializer: repeatedKeyParamsSerializer,
    responseType: "blob",
    timeout: FATP_CANDIDATE_LOGS_TIMEOUT_MS,
    useSystemLoading: false,
    // Also suppress the global system toast: the shared response interceptor now recovers the
    // backend's message from a blob error body and shows it there, and the button's own handler
    // shows it too — leaving both on produces two toasts for one failure, one of them the generic
    // axios text. The component owns this message because the failure is scoped to one check's
    // button.
    useSystemMessage: false,
  });

export const save_ReadoutInput = (data: TSaveReadoutRequest) => http.post<IServerResponse>(apiConfig.save_ReadoutInput, data, { useSysRefresh: true });

// WARN: Mock data for integration use only
// mock.ts is the correct file that used in axiosMockInstance;
// However, for production, there are some API that not ready, this is the place to mock them in production.
// Which means this need to be """removed""" when BE is ready.
// Mock: includes `activated` for Cognito activation gating + roles.
// When BE is ready, uncomment the real http.get call above and remove this mock.
// eslint-disable-next-line no-unused-vars
export const fetch_UserList = (tag: string) => Promise.resolve({ data: [{ username: "jianyihp", login: "jianyihp" }] } as TUserListResponse);

// Homepage
export const fetch_Home_TopCardsDisplay = () => http.get(apiConfig.fetch_Home_TopCardsDisplay, { useSystemLoading: false });
// loadInactiveBuilds=true also returns completed/inactive builds; default (omitted) returns active only.
export const fetch_Home_BuildDisplay = (loadInactiveBuilds = false) => http.get(apiConfig.fetch_Home_BuildDisplay, { params: loadInactiveBuilds ? { loadInactiveBuilds: true } : undefined, useSystemLoading: false });
// Toggles favorite for a SINGLE build (idempotent). Build identifiers + isFavorite target state
// ride in the request body.
export const set_Home_FavoriteBuilds = (data: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string; isFavorite: boolean }) =>
  http.post(apiConfig.set_Home_FavoriteBuilds, data, { useSystemLoading: false });
export const fetch_Home_BuildOwners = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) => http.get(apiConfig.fetch_Home_BuildOwners, { params, useSystemLoading: false });
// Email preferences/sends per the homepage_email contract. get_email_frequency is a GET
// (identifiers as @httpQuery params); set_email_frequency sends its data in the POST body.
export const get_Home_EmailFrequency = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) =>
  // Passive read on drawer-open — suppress the system success toast so it doesn't nag the user.
  http.get(apiConfig.get_Home_EmailFrequency, { params, useSystemLoading: false, useSystemMessage: false });
export const set_Home_EmailFrequency = (data: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string; frequency: string }) =>
  http.post(apiConfig.set_Home_EmailFrequency, data, { useSystemLoading: false });
// Passive read on drawer-open: reports whether the calling user is on the build's email recipient
// list. Response carries `subscribed: boolean` at the top level (restJson1 flat shape). No system
// toast — same rationale as get_email_frequency.
export const get_Home_EmailSubscriptionStatus = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) =>
  http.get(apiConfig.get_Home_EmailSubscriptionStatus, { params, useSystemLoading: false, useSystemMessage: false });
export const subscribe_Home_BuildEmail = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) =>
  http.post(apiConfig.subscribe_Home_BuildEmail, null, { params, useSystemLoading: false });
export const unsubscribe_Home_BuildEmail = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) =>
  http.post(apiConfig.unsubscribe_Home_BuildEmail, null, { params, useSystemLoading: false });
export const send_Home_EmailToMe = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) => http.post(apiConfig.send_Home_EmailToMe, null, { params, useSystemLoading: false });
export const send_Home_EmailToTeam = (params: { familyId?: string; projectId?: string; testLocationId?: string; phaseId?: string; buildId?: string }) => http.post(apiConfig.send_Home_EmailToTeam, null, { params, useSystemLoading: false });

// FA Tracker
export const fetch_FATracker_ProjectList = () => http.get<IServerResponse<{ id: string; name: string }[]>>(apiConfig.fetch_FATracker_ProjectList);

export const fetch_FATracker_Summary = (data: { projectId: string }) => http.get<IServerResponse<IFATrackerSummary>>(apiConfig.fetch_FATracker_Summary, { params: data, useSystemLoading: false });

export const fetch_FATracker_FALog = (data: { projectId: string; searchQuery?: string; statusFilter?: string; page?: number; pageSize?: number }) => http.get<TFALogListResponse>(apiConfig.fetch_FATracker_FALog, { params: data });

export const fetch_FATracker_NewFindings = (data: { projectId: string; searchQuery?: string; page?: number; pageSize?: number } & INewFindingsFilterParams) =>
  http.get<TNewFindingsListResponse>(apiConfig.fetch_FATracker_NewFindings, { params: data, paramsSerializer: repeatedKeyParamsSerializer });

// Companion "recommendations" op for the New Findings filter bar: available values in ONE dimension
// across the whole project scope, cascading on the OTHER dimensions' selected ids. Cheap enough to
// call on every dropdown open (no attachment URL signing), so no system loading spinner.
export const fetch_FATracker_NewFindingsFilterOptions = (data: { projectId: string; testLocationId?: string; searchQuery?: string; dimension: string } & INewFindingsFilterParams) =>
  http.get<TNewFindingsFilterOptionsResponse>(apiConfig.fetch_FATracker_NewFindingsFilterOptions, { params: data, paramsSerializer: repeatedKeyParamsSerializer, useSystemLoading: false });

export const fetch_FATracker_FADetail = (data: { projectId: string; faCaseId: string }) => http.get<TFADetailResponse>(apiConfig.fetch_FATracker_FADetail, { params: data });

export const update_FATracker_FADetail = (data: TUpdateFADetailRequest) => http.post<IServerResponse>(apiConfig.update_FATracker_FADetail, data, { useSysRefresh: true });

export const update_FATracker_UpdateAll = (data: TUpdateAllFARecordsRequest) => http.post<TUpdateAllFARecordsResponse>(apiConfig.update_FATracker_UpdateAll, data, { useSysRefresh: true });

export const create_FATracker_FA = (data: TCreateFARequest) => http.post<TCreateFARecordResponse>(apiConfig.create_FATracker_FA, data, { useSysRefresh: true });

export const create_FATracker_MarkWaived = (data: TMarkWaivedRequest) => http.post<IServerResponse>(apiConfig.create_FATracker_MarkWaived, data, { useSysRefresh: true });

export const fetch_FATracker_SmartSummary = (data: TSmartSummaryRequest) => http.get<TSmartSummaryResponse>(apiConfig.fetch_FATracker_SmartSummary, { params: data, useSystemLoading: false });

export const batch_FATracker_SmartSummary = (data: TBatchSmartSummaryRequest) => http.post<TBatchSmartSummaryResponse>(apiConfig.batch_FATracker_SmartSummary, data, { useSystemLoading: false });
export const unlink_FATracker_Finding = (data: TUnlinkFindingRequest) => http.post<IServerResponse>(apiConfig.unlink_FATracker_Finding, data, { useSysRefresh: true });

// FA Tracker — File Upload (for attachments)
export const upload_FATracker_Attachment = (data: { file: File; projectId: string; testLocationId?: string; faCaseId: string; fileName: string }) => {
  const formData = new FormData();
  formData.append("file", data.file);
  const params = new URLSearchParams();
  params.append("projectId", data.projectId);
  if (data.testLocationId) params.append("testLocationId", data.testLocationId);
  params.append("faCaseId", data.faCaseId);
  params.append("fileName", data.fileName);
  return http.post<IServerResponse<{ s3Path: string }>>(`${apiConfig.upload_File}?${params.toString()}`, formData, { headers: { "Content-Type": "multipart/form-data" }, useSystemLoading: false });
};

// Data Overview
export const fetch_DataOverview_List = (data: TIdentifierObject) => http.get<TDataOverviewListResponse>(apiConfig.fetch_DataOverview_List, { params: data });

// Data Overview — Phase 2 GETs
export const fetch_DataOverview_ReadoutList = (data: TIdentifierObject & { sequenceId: string; stationId?: string }) =>
  http.get<TReadoutListResponse>(apiConfig.fetch_DataOverview_ReadoutList, { params: data, useSysRefresh: false, useSystemLoading: false });
export const fetch_DataOverview_TestVariableSelections = (data: TIdentifierObject) => http.get<TTestVariableSelectionsResponse>(apiConfig.fetch_DataOverview_TestVariableSelections, { params: data });
// Major + final readouts (with their scheduled dates) for the Update Schedule drawer.
// Scoped to a test/sequence (no dsn) — replaces get_device_checkpoint_dates + the
// client-side major-readout filter of get_readout_list.
export const fetch_DataOverview_MajorReadoutList = (data: TIdentifierObject & { sequenceId: string }) => http.get<TMajorReadoutListResponse>(apiConfig.fetch_DataOverview_MajorReadoutList, { params: data, useSystemLoading: false });
// FATP Log Link drawer — "station" is a FATP check. Check list = GET /get_fatp_checks.
export const fetch_DataOverview_FATPStations = (data: TIdentifierObject) => http.get<TFATPCheckOptionsResponse>(apiConfig.fetch_DataOverview_FATPStations, { params: data, useSysRefresh: false, useSystemLoading: false });
// FATP-check-filtered readouts = GET /get_fatp_readouts (required checkCode). Unlike
// get_readout_list (which ignores checkCode), this returns only readouts mapped to the
// check, so picking one can't violate the readout↔check FK on save. The BE already groups
// each readout's pairs into `keyValuePairs`, so a row maps 1:1 to the IReadoutListItem the
// drawer's dropdown consumes (we just rename keyValuePairs → keyValuePair).
export const fetch_DataOverview_FATPReadouts = (data: TIdentifierObject & { checkCode: string; sequenceId: string }): Promise<TReadoutListResponse> =>
  http.get<TFatpReadoutsResponse>(apiConfig.fetch_DataOverview_FATPReadouts, { params: data, useSysRefresh: false, useSystemLoading: false }).then(res => ({
    ...res,
    data: (res.data ?? []).map(item => ({
      id: item.id,
      order: item.order,
      keyValuePair: item.keyValuePairs ?? [],
      isMajorCheckpoint: false,
    })),
  }));

// Genealogy records per device = POST /get_batch_fatp_genealogy.
export const search_DataOverview_FATPLogs = (data: TFATPGenealogyRequest) => http.post<TFATPGenealogyResponse>(apiConfig.search_DataOverview_FATPLogs, data, { useSysRefresh: false, useSystemLoading: false });

// Data Overview — Phase 2 batch POSTs
export const batch_DataOverview_MarkOngoing = (data: TBatchMarkOngoingRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_MarkOngoing, data, { useSysRefresh: true });
export const batch_DataOverview_MarkCompleted = (data: TBatchMarkCompletedRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_MarkCompleted, data, { useSysRefresh: true });
export const batch_DataOverview_Reset = (data: TBatchResetRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_Reset, data, { useSysRefresh: true });
export const batch_DataOverview_Approve = (data: TBatchApproveRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_Approve, data, { useSysRefresh: true });
export const batch_DataOverview_PassReadouts = (data: TBatchPassReadoutsRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_PassReadouts, data, { useSysRefresh: true });
export const batch_DataOverview_SetVariables = (data: TBatchSetVariablesRequest) => http.post<TBatchActionResponse>(apiConfig.batch_DataOverview_SetVariables, data, { useSysRefresh: true });
// Upsert readout schedule dates = POST /change_readout_schedule. The BE input model
// requires ISO yyyy-mm-dd (pattern \d{4}-\d{2}-\d{2}), which is exactly what the UI date
// picker emits — forward it unchanged. (Converting to yyyy/MM/dd here previously tripped
// the BE regex validation: "checkpoints.N.member.date failed to satisfy constraint".)
export const change_DataOverview_ReadoutSchedule = (data: TChangeReadoutScheduleRequest) => http.post<TBatchActionResponse>(apiConfig.change_DataOverview_ReadoutSchedule, data, { useSysRefresh: true });
// Persist FATP mapping = POST /save_batch_fatp_mapping.
export const batch_DataOverview_LinkFATPLogs = (data: TSaveBatchFatpMappingRequest) => http.post<TSaveBatchFatpMappingResponse>(apiConfig.batch_DataOverview_LinkFATPLogs, data, { useSysRefresh: true });
export const update_DataOverview_TestStatus = (data: TUpdateTestStatusRequest) => http.post<TBatchActionResponse>(apiConfig.update_DataOverview_TestStatus, data, { useSysRefresh: true });

// ─── FATPViewer ─────────────────────────────────────────────────────────
export const fetch_Station_List = (data: TIdentifierParams | undefined) => http.get<TIdentifierResponse>(apiConfig.fetch_Station_List, { params: data, useSystemLoading: false });

// get_fatp_configs — POST: all fields in the body. The deployed BE reads checkCode +
// testReadoutIds from the JSON body, not the query string (the Smithy @httpQuery binding
// does not hold for this operation — verified against Alpha, query-only returns 500).
export const fetch_FATPViewer_DeviceBuckets = (data: TFATPConfigsRequest) => http.post<TFATPConfigsResponse>(apiConfig.fetch_FATPViewer_DeviceBuckets, data, { useSystemLoading: false });

// get_fatp_parameters — POST: identifiers + checkCode + dsns + testReadoutIds, all in the body.
export const fetch_FATPViewer_Parameters = (data: TFATPParametersRequest) => http.post<TFATPParametersResponse>(apiConfig.fetch_FATPViewer_Parameters, data, { useSystemLoading: false });

// get_fatp_parameter_data — POST: identifiers + checkCode + dsns + testReadoutIds + parameters, all in the body.
export const fetch_FATPViewer_StationData = (data: TFATPParameterDataRequest) => http.post<TFATPParameterDataResponse>(apiConfig.fetch_FATPViewer_StationData, data);

// start_fatp_raw_export — POST: identifiers + checkCode + dsns + testReadoutIds, all in the
// body, for the same reason as get_fatp_configs above. `checkCode` in particular reads like a
// query param in the Smithy model but is a plain body member: the deployed stack takes POST
// fields from the JSON body only. Returns a jobId immediately — the export itself runs
// asynchronously because a realistic selection takes ~70s to build, past the 60s ALB idle
// timeout. useSystemLoading: false — see the poll below.
export const start_FATPViewer_RawExport = (data: TFATPRawExportRequest) => http.post<TFATPRawExportSubmissionResponse>(apiConfig.start_FATPViewer_RawExport, data, { useSystemLoading: false });

// get_fatp_raw_export_status — GET with jobId as a query param. This operation genuinely is
// @httpQuery, the exact inverse of the POST above. Polled every couple of seconds for the whole
// life of the job, so useSystemLoading: false is not optional: the default `true` would hold the
// app-wide blurred GlobalLoading overlay up for the entire multi-minute export.
export const fetch_FATPViewer_RawExportStatus = (jobId: string) => http.get<TFATPRawExportStatusResponse>(apiConfig.fetch_FATPViewer_RawExportStatus, { params: { jobId }, useSystemLoading: false });

// get_fatp_readout_tree — GET returns the readouts already nested as a tests → readouts
// tree (IFATPViewerReadoutTreeTest[]). We wrap it into { tests } so callers keep receiving
// the IFATPViewerReadoutTree shape the panels consume.
export const fetch_FATPViewer_ReadoutTree = (data: TIdentifierParams | undefined) =>
  http.get<TFATPViewerReadoutTreeResponse>(apiConfig.fetch_FATPViewer_ReadoutTree, { params: data, useSystemLoading: false }).then(res => ({
    ...res,
    data: { tests: res.data ?? [] },
  }));

// ─── UV Visualizer ──────────────────────────────────────────────────────────
// GetUvColorTable (BE: GET /api/get_color_table). Sends the standard identifier
// fields as flat query params and unwraps the BaseResponse envelope so callers
// receive the UvComponentItemList array directly.
export const fetch_UVVisualizer_ColorTable = (identifier: TIdentifierObject): Promise<TUvColorTableResponse> =>
  http
    .get<TUVVisualizerColorTableResponse>(apiConfig.fetch_UVVisualizer_ColorTable, {
      params: {
        testLocationId: identifier.testLocationId ?? "",
        familyId: identifier.familyId ?? "",
        projectId: identifier.projectId ?? "",
        phaseId: identifier.phaseId ?? "",
        buildId: identifier.buildId ?? "",
      },
      useSystemLoading: false,
    })
    .then(res => res.data ?? []);
// ── ESD Calculator ────────────────────────────────────────────────────────────
export const predict_ESD_Reliability = (data: IESDPredictRequest) => http.post<TESDPredictResponse>(apiConfig.predict_ESD_Reliability, data);
export const generate_ESD_TestPlan = (data: IESDGenerateTestPlanRequest) => http.post<TESDGenerateTestPlanResponse>(apiConfig.generate_ESD_TestPlan, data);
export const fetch_ESD_ProjectData = (data: IESDProjectDataRequest) =>
  http.get<TESDProjectDataResponse>(apiConfig.fetch_ESD_ProjectData, {
    params: data,
    paramsSerializer: params => {
      const parts: string[] = [];
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
        } else if (value !== undefined && value !== null && value !== "") {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
export const import_ESD_TestPlan = (data: IESDImportTestPlanRequest) => http.post<TESDImportTestPlanResponse>(apiConfig.import_ESD_TestPlan, data);
export const save_ESD_Settings = (data: IESDSaveSettingsRequest) => http.post<TESDSaveSettingsResponse>(apiConfig.save_ESD_Settings, data);
export const fetch_ESD_Settings = (data: IESDGetSettingsRequest) => http.get<TESDGetSettingsResponse>(apiConfig.fetch_ESD_Settings, { params: data, useSystemLoading: false });

// ── Ball Impact Calculator ────────────────────────────────────────────────────
export interface IBallImpactProjectDataRequest {
  familyId: string;
  projectId: string;
  testLocationId: string;
  phaseId: string;
  buildId: string;
  productLine: string;
}

export interface IBallImpactSampleResponse {
  deviceId: string;
  shortId?: string; // DSN-derived short display id (build-config short_id override, else DSN tail)
  config: string;
  testedImpacts?: number[]; // Tablet/eReader: numeric grid-point indices tested
  failedImpacts?: number[]; // Tablet/eReader/Fire TV Stick: numeric failed points
  testedPoints?: number; // Fire TV Stick: total count of tested points
  failedPoints?: string[]; // SMP/Echo: named failure point IDs
  locationImpacts?: Record<string, number>; // SMP/Echo: point-name → impact-count map
}

export interface IBallImpactProjectDataResponse {
  data: {
    productLine: string;
    devicesTested: number;
    samples: IBallImpactSampleResponse[];
  };
  isSuccess: boolean;
  code: number;
}

export const fetch_BallImpact_ProjectData = (data: IBallImpactProjectDataRequest) => http.get<IBallImpactProjectDataResponse>(apiConfig.fetch_BallImpact_ProjectData, { params: data });

// ── Stress-Strength Calculator ────────────────────────────────────────────────
export interface IStressStrengthImportDataRequest {
  testLocationId?: string;
  familyId?: string;
  projectId?: string;
  phaseId?: string;
  buildId?: string;
  testCode?: string;
  issueId?: number;
}

export interface IStressStrengthImportDataRow {
  count: number;
  status: string; // e.g. "PASS" | "FAIL" — normalized client-side to "failed"/"suspended"
  prevTime: number;
  currTime: number;
}

export interface IStressStrengthImportDataItem {
  issueId: number;
  issueDescription: string;
  dataRows: IStressStrengthImportDataRow[];
}

export interface IStressStrengthImportDataResponse {
  data: IStressStrengthImportDataItem[];
  isSuccess: boolean;
  code: number;
  message?: string;
}

/**
 * Retrieve strength distribution sample data for a given build + test code +
 * optional issue, used to prefill the "Custom Data (Fit Model)" rows in the
 * Stress-Strength Calculator. Returns one entry per issue with its rows.
 */
export const fetch_StressStrength_ImportData = (data: IStressStrengthImportDataRequest) => http.get<IStressStrengthImportDataResponse>(apiConfig.fetch_StressStrength_ImportData, { params: data, useSystemLoading: false });

// ── Approval Center ─────────────────────────────────────────────────────────

export interface IApprovalListParams {
  tab?: "PENDING" | "HISTORY";
  myItemsOnly?: boolean;
  source?: "ALL" | "BUILD" | "FAMILY" | "SIGNOFF" | "COMMITTEE";
  projectId?: string;
  searchText?: string;
}

export const fetch_Approval_List = (params: IApprovalListParams) => http.get(apiConfig.fetch_Approval_List, { params });

export const fetch_Approval_Detail = (approvalId: number) => http.get(apiConfig.fetch_Approval_Detail, { params: { approvalId } });

export interface ISubmitForApprovalParams {
  familyId?: string;
  projectId: string;
  testLocationId: string;
  phaseId: string;
  buildId: string;
  testId: string;
  type?: string;
  approvers: string[];
  comment?: string;
}

export const post_Approval_Submit = (data: ISubmitForApprovalParams) => http.post(apiConfig.post_Approval_Submit, data);

export interface ISubmitBuildSignOffParams {
  familyId?: string;
  projectId: string;
  testLocationId: string;
  phaseId: string;
  buildId: string;
  approvers: string[];
  comment?: string;
}

export const post_Approval_SubmitBuildSignOff = (data: ISubmitBuildSignOffParams) => http.post(apiConfig.post_Approval_SubmitBuildSignOff, data);

export const post_Approval_Approve = (data: { approvalId: number; comment?: string }) => http.post(apiConfig.post_Approval_Approve, data);

export const post_Approval_Reject = (data: { approvalId: number; comment: string }) => http.post(apiConfig.post_Approval_Reject, data);

export const post_Approval_Recall = (data: { approvalId: number }) => http.post(apiConfig.post_Approval_Recall, data);

export const post_Approval_AddApprover = (data: { approvalId: number; approverAlias: string }) => http.post(apiConfig.post_Approval_AddApprover, data);

export const post_Approval_Transfer = (data: { approvalId: number; toAlias: string }) => http.post(apiConfig.post_Approval_Transfer, data);

export const post_Approval_BatchApprove = (data: { approvalIds: number[]; comment?: string }) => http.post<TBatchApproveResponse>(apiConfig.post_Approval_BatchApprove, data);

// Equipment Booking
export const fetch_EquipmentBooking_Equipment = (data: IGetEquipmentParams) => http.get<TGetEquipmentResponse>(apiConfig.fetch_EquipmentBooking_Equipment, { params: data, useSystemLoading: false });
export const fetch_EquipmentBooking_Bookings = (data: IGetBookingsParams) => http.get<TGetBookingsResponse>(apiConfig.fetch_EquipmentBooking_Bookings, { params: data, useSystemLoading: false });
// The booking Project dropdown reuses the shared family-and-project endpoint, filtered to projects
// that have a test waterfall at the selected CM location (server-side).
export const fetch_EquipmentBooking_Projects = (cmLocation: string) => http.get<TIdentifierResponse>(apiConfig.fetch_Project_List, { params: { filter: "withTestWaterfall", cmLocation }, useSystemLoading: false });
export const create_EquipmentBooking = (data: ICreateBookingRequest) => http.post<TCreateBookingResponse>(apiConfig.create_EquipmentBooking, data, { useSysRefresh: true });
export const update_EquipmentBooking = (data: IUpdateBookingRequest) => http.post<IServerResponse>(apiConfig.update_EquipmentBooking, data, { useSysRefresh: true });
export const cancel_EquipmentBooking = (data: ICancelBookingRequest) => http.post<IServerResponse>(apiConfig.cancel_EquipmentBooking, data, { useSysRefresh: true });
export const update_EquipmentBooking_Status = (data: IUpdateStatusRequest) => http.post<IServerResponse>(apiConfig.update_EquipmentBooking_Status, data, { useSysRefresh: true });
